/**
 * Record invariants. IndexedDB has no CHECK constraints, so every write goes through these
 * functions first (repositories, backup restore, sync-package import). They are the same rules
 * the revision-1 Postgres schema enforced, plus the income ledger's.
 */
import {
  AccountingPeriodError,
  accountingMonthFromDate,
  computeAccountingMonth,
  parseAccountingMonth,
  parseIsoDate,
} from "./accounting-period";
import { EXPENSE_ACCOUNTING_RULES, EXPENSE_SOURCES, EXPENSE_STATUSES, SYNC_STATUSES, CATEGORY_SOURCES } from "./expense";
import { INCOME_ACCOUNTING_RULES, INCOME_SOURCES, INCOME_STATUSES, INCOME_TYPES } from "./income";
import { DUPLICATE_STATUSES, type BalanceAnchor, type BudgetLine, type Category, type Expense, type Income, type IncomeBudget, type PaymentMethod } from "./model";
import { Decimal, hasAtMostTwoDecimals } from "./money";

export class ValidationError extends Error {
  override readonly name = "ValidationError";
  constructor(
    readonly entity: string,
    readonly issues: string[],
  ) {
    super(`${entity} non valido: ${issues.join("; ")}`);
  }
}

export interface ReferenceContext {
  categories: ReadonlyMap<string, Category>;
  paymentMethods: ReadonlyMap<string, PaymentMethod>;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isDecimal(value: string): boolean {
  if (typeof value !== "string" || !/^-?\d+(\.\d+)?$/.test(value)) return false;
  return !new Decimal(value).isNaN();
}

function checkDate(issues: string[], label: string, value: string): boolean {
  try {
    parseIsoDate(value);
    return true;
  } catch {
    issues.push(`${label} non valida (${value})`);
    return false;
  }
}

function checkMonth(issues: string[], label: string, value: string): boolean {
  try {
    parseAccountingMonth(value);
    return true;
  } catch {
    issues.push(`${label} non valido (${value})`);
    return false;
  }
}

function checkPositiveAmount(issues: string[], label: string, value: string, twoDecimals: boolean): void {
  if (!isDecimal(value)) {
    issues.push(`${label} non è un numero decimale (${value})`);
    return;
  }
  if (new Decimal(value).lte(0)) issues.push(`${label} deve essere maggiore di zero`);
  if (twoDecimals && !hasAtMostTwoDecimals(value)) issues.push(`${label} ha più di due decimali`);
}

function checkIn<T extends string>(issues: string[], label: string, value: T, allowed: readonly string[]): void {
  if (!allowed.includes(value)) issues.push(`${label} sconosciuto (${value})`);
}

export function expenseIssues(e: Expense, ctx: ReferenceContext, { requireActiveCategory = true } = {}): string[] {
  const issues: string[] = [];
  if (!UUID.test(e.id)) issues.push("id non valido");
  if (!Number.isInteger(e.version) || e.version < 1) issues.push("versione non valida");
  checkIn(issues, "stato", e.status, EXPENSE_STATUSES);
  checkIn(issues, "origine", e.source, EXPENSE_SOURCES);
  checkIn(issues, "regola contabile", e.accountingRule, EXPENSE_ACCOUNTING_RULES);
  checkIn(issues, "origine categoria", e.categorySource, CATEGORY_SOURCES);
  checkIn(issues, "stato sincronizzazione", e.sync.status, SYNC_STATUSES);

  const imported = e.source === "excel_import";
  checkPositiveAmount(issues, "Importo", e.amount, !imported);
  checkPositiveAmount(issues, "Importo in euro", e.amountEur, !imported);
  if (!/^[A-Z]{3}$/.test(e.currency)) issues.push(`valuta non valida (${e.currency})`);
  if (e.currency === "EUR" && isDecimal(e.amount) && isDecimal(e.amountEur) && !new Decimal(e.amount).eq(e.amountEur)) {
    issues.push("per gli importi in EUR l'importo in euro deve coincidere con l'importo");
  }

  const dateOk = checkDate(issues, "Data di acquisto", e.purchaseDate);
  const monthOk = checkMonth(issues, "Mese contabile", e.accountingMonth);
  if (dateOk && monthOk) {
    if (e.accountingRule === "same_month" || e.accountingRule === "next_month") {
      const expected = computeAccountingMonth({ purchaseDate: e.purchaseDate, rule: e.accountingRule }).accountingMonth;
      if (expected !== e.accountingMonth) {
        issues.push(`mese contabile ${e.accountingMonth} incoerente con la regola ${e.accountingRule} (atteso ${expected})`);
      }
    }
  }
  if (e.accountingRule === "legacy" && !imported) issues.push("la regola 'legacy' è riservata alle righe importate da Excel");

  if (e.paymentMethodId === null) {
    if (!imported && e.status !== "draft") issues.push("metodo di pagamento obbligatorio");
  } else if (!ctx.paymentMethods.has(e.paymentMethodId)) {
    issues.push("metodo di pagamento inesistente");
  }

  const category = ctx.categories.get(e.categoryId);
  if (!category) issues.push("categoria inesistente");
  else if (requireActiveCategory && !category.isActive) issues.push(`la categoria "${category.excelLabel}" non è più in Elenco Spese`);

  if (e.status === "confirmed" && !e.confirmedAt) issues.push("manca la data di conferma");
  if (e.status === "void" && !e.voidedAt) issues.push("manca la data di annullamento");
  if (e.status === "draft" && e.sync.status !== "not_queued") issues.push("una bozza non può essere sincronizzata");

  const d = e.duplicate;
  if (!d || !Array.isArray(d.matches)) {
    issues.push("controllo duplicati mancante");
  } else {
    checkIn(issues, "stato duplicato", d.status, DUPLICATE_STATUSES);
    if (d.status === "none" && d.matches.length > 0) issues.push("stato duplicato 'none' con corrispondenze registrate");
    if (d.status !== "none" && d.matches.length === 0) issues.push("stato duplicato senza corrispondenze");
    if (d.status === "kept" && !d.resolvedAt) issues.push("manca la data della decisione sul duplicato");
    if (d.matches.some((m) => m.expenseId === e.id)) issues.push("una spesa non può essere duplicata di se stessa");
  }
  return issues;
}

export function incomeIssues(i: Income): string[] {
  const issues: string[] = [];
  if (!UUID.test(i.id)) issues.push("id non valido");
  if (!Number.isInteger(i.version) || i.version < 1) issues.push("versione non valida");
  checkIn(issues, "stato", i.status, INCOME_STATUSES);
  checkIn(issues, "origine", i.source, INCOME_SOURCES);
  checkIn(issues, "tipo", i.type, INCOME_TYPES);
  checkIn(issues, "regola contabile", i.accountingRule, INCOME_ACCOUNTING_RULES);
  checkIn(issues, "stato sincronizzazione", i.sync.status, SYNC_STATUSES);

  const imported = i.source === "excel_import";
  checkPositiveAmount(issues, "Importo", i.amount, !imported);
  if (typeof i.description !== "string" || i.description.trim() === "") issues.push("descrizione obbligatoria");

  const dateOk = checkDate(issues, "Data", i.date);
  const monthOk = checkMonth(issues, "Mese contabile", i.accountingMonth);
  if (dateOk && monthOk && i.accountingRule === "same_month" && accountingMonthFromDate(i.date) !== i.accountingMonth) {
    issues.push(`mese contabile ${i.accountingMonth} incoerente con la data ${i.date}`);
  }
  if (i.accountingRule === "legacy" && !imported) issues.push("la regola 'legacy' è riservata alle entrate importate da Excel");
  if (i.status === "void" && !i.voidedAt) issues.push("manca la data di annullamento");
  return issues;
}

export function budgetLineIssues(b: BudgetLine, ctx: Pick<ReferenceContext, "categories">): string[] {
  const issues: string[] = [];
  checkMonth(issues, "Mese", b.accountingMonth);
  if (b.id !== `${b.accountingMonth}|${b.categoryId}`) issues.push("id della riga di budget non coerente");
  if (!ctx.categories.has(b.categoryId)) issues.push("categoria inesistente");
  if (!isDecimal(b.amount) || new Decimal(b.amount).lt(0)) issues.push("il budget deve essere un importo non negativo");
  return issues;
}

export function incomeBudgetIssues(b: IncomeBudget): string[] {
  const issues: string[] = [];
  checkMonth(issues, "Mese", b.accountingMonth);
  if (b.id !== b.accountingMonth) issues.push("id non coerente con il mese");
  for (const [label, v] of [["Stipendio previsto", b.salary], ["Extra previste", b.extra]] as const) {
    if (!isDecimal(v) || new Decimal(v).lt(0)) issues.push(`${label} deve essere un importo non negativo`);
  }
  return issues;
}

export function balanceAnchorIssues(a: BalanceAnchor): string[] {
  const issues: string[] = [];
  checkMonth(issues, "Mese", a.effectiveMonth);
  if (!isDecimal(a.amount)) issues.push("il saldo deve essere un numero decimale (anche negativo)");
  if (!a.label.trim()) issues.push("descrizione obbligatoria");
  return issues;
}

export function assertValid(entity: string, issues: string[]): void {
  if (issues.length > 0) throw new ValidationError(entity, issues);
}

export { AccountingPeriodError };
